import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import api from '@/services/api'
import router from '@/router'

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref(null)
  const token = ref(localStorage.getItem('token') || null)
  const refreshToken = ref(localStorage.getItem('refreshToken') || null)
  const loading = ref(false)
  const error = ref(null)

  // Getters
  const isAuthenticated = computed(() => !!token.value && !!user.value)
  const isAdmin = computed(() => user.value?.role === 'admin' || user.value?.role === 'developer')
  const isDeveloper = computed(() => user.value?.role === 'developer')
  const userRole = computed(() => user.value?.role || 'guest')

  // Actions
  async function login(email, password) {
    loading.value = true
    error.value = null
    try {
      const response = await api.post('/api/v1/auth/login', { email, password })
      const { token: accessToken, refresh_token, user: userData } = response.data.data

      setTokens(accessToken, refresh_token)
      user.value = userData

      return { success: true }
    } catch (err) {
      error.value = err.response?.data?.message || 'Login failed'
      return { success: false, error: error.value }
    } finally {
      loading.value = false
    }
  }

  async function register(data) {
    loading.value = true
    error.value = null
    try {
      const response = await api.post('/api/v1/auth/register', data)
      const { token: accessToken, refresh_token, user: userData } = response.data.data

      setTokens(accessToken, refresh_token)
      user.value = userData

      return { success: true }
    } catch (err) {
      error.value = err.response?.data?.message || 'Registration failed'
      return { success: false, error: error.value }
    } finally {
      loading.value = false
    }
  }

  async function logout() {
    try {
      await api.post('/api/v1/auth/logout')
    } catch (err) {
      // Ignore logout errors
    } finally {
      clearAuth()
      router.push('/login')
    }
  }

  async function fetchProfile() {
    if (!token.value) return

    loading.value = true
    try {
      const response = await api.get('/api/v1/auth/me')
      user.value = response.data.data
    } catch (err) {
      if (err.response?.status === 401) {
        await refreshAccessToken()
      }
    } finally {
      loading.value = false
    }
  }

  async function updateProfile(data) {
    loading.value = true
    error.value = null
    try {
      const response = await api.put('/api/v1/auth/profile', data)
      user.value = response.data.data
      return { success: true }
    } catch (err) {
      error.value = err.response?.data?.message || 'Update failed'
      return { success: false, error: error.value }
    } finally {
      loading.value = false
    }
  }

  async function changePassword(currentPassword, newPassword) {
    loading.value = true
    error.value = null
    try {
      await api.post('/api/v1/auth/change-password', {
        current_password: currentPassword,
        new_password: newPassword
      })
      return { success: true }
    } catch (err) {
      error.value = err.response?.data?.message || 'Password change failed'
      return { success: false, error: error.value }
    } finally {
      loading.value = false
    }
  }

  async function refreshAccessToken() {
    if (!refreshToken.value) {
      clearAuth()
      return false
    }

    try {
      const response = await api.post('/api/v1/auth/refresh', {
        refresh_token: refreshToken.value
      })
      const { token: accessToken, refresh_token: newRefreshToken } = response.data.data

      setTokens(accessToken, newRefreshToken)
      await fetchProfile()
      return true
    } catch (err) {
      clearAuth()
      return false
    }
  }

  function setTokens(accessToken, newRefreshToken) {
    token.value = accessToken
    refreshToken.value = newRefreshToken
    localStorage.setItem('token', accessToken)
    localStorage.setItem('refreshToken', newRefreshToken)
  }

  function clearAuth() {
    user.value = null
    token.value = null
    refreshToken.value = null
    localStorage.removeItem('token')
    localStorage.removeItem('refreshToken')
  }

  // Initialize: fetch profile if token exists
  function init() {
    if (token.value) {
      fetchProfile()
    }
  }

  return {
    // State
    user,
    token,
    loading,
    error,
    // Getters
    isAuthenticated,
    isAdmin,
    isDeveloper,
    userRole,
    // Actions
    login,
    register,
    logout,
    fetchProfile,
    updateProfile,
    changePassword,
    refreshAccessToken,
    clearAuth,
    init
  }
})
